import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const socket = io("https://webrtc.d-rosa.ai", {
  path: "/socket.io/",
  transports: ["websocket"],
  secure: true,
});

const BT_SERVICE_UUID = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const BT_CHAR_TX_UUID = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";

async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ name: "ROSA_RELAY" }],
    optionalServices: [BT_SERVICE_UUID],
  });
  const server  = await device.gatt.connect();
  const service = await server.getPrimaryService(BT_SERVICE_UUID);
  const char    = await service.getCharacteristic(BT_CHAR_TX_UUID);
  return { device, char };
}

async function sendBTCommand(char, command) {
  const encoder = new TextEncoder();
  await char.writeValue(encoder.encode(command));
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function Run() {
  const localVideoRef        = useRef(null);
  const remoteVideoRef       = useRef(null);
  const captureCanvasRef     = useRef(null);
  const pcRef                = useRef(null);
  const localStreamRef       = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const screenTrackRef       = useRef(null);
  const btCharRef            = useRef(null);

  const [room, setRoom]               = useState("");
  const [joined, setJoined]           = useState(false);
  const [initiator, setInitiator]     = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [relayStates, setRelayStates] = useState(Array(8).fill(false));
  const [disabledRelays, setDisabledRelays] = useState(Array(8).fill(false));
  const [boundaryMessage, setBoundaryMessage] = useState("");
  const [commandLog, setCommandLog]   = useState([]);
  const [btConnected, setBtConnected] = useState(false);
  const [btConnecting, setBtConnecting] = useState(false);
  const [btDevice, setBtDevice]       = useState(null);

  // -- Comparison state -------------------------------------------------------
  const [activeRelay, setActiveRelay] = useState(null);
  const [result, setResult]           = useState("Waiting...");
  const [similarity, setSimilarity]   = useState(null);

  const [user] = useState("user-" + Math.floor(Math.random() * 10000));
  const isMobile = useIsMobile();

  const log = (msg) =>
    setCommandLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  // -- Bluetooth --------------------------------------------------------------
  const handleBTConnect = async () => {
    if (!navigator.bluetooth) {
      log("Web Bluetooth not supported in this browser");
      return;
    }
    try {
      setBtConnecting(true);
      log("Searching for ROSA_RELAY�");
      const { device, char } = await connectBluetooth();
      btCharRef.current = char;
      setBtDevice(device);
      setBtConnected(true);
      log(`Bluetooth connected to ${device.name}`);
      device.addEventListener("gattserverdisconnected", () => {
        btCharRef.current = null;
        setBtConnected(false);
        log("Bluetooth disconnected");
      });
    } catch (err) {
      log(`BT Error: ${err.message}`);
    } finally {
      setBtConnecting(false);
    }
  };

  const handleBTDisconnect = () => {
    btDevice?.gatt?.disconnect();
    btCharRef.current = null;
    setBtConnected(false);
    setBtDevice(null);
    log("Bluetooth disconnected by user");
  };

  // -- Capture & send remote frame to backend (every 5s while joined) ---------
  const captureAndSendFrame = async () => {
    try {
      const video  = remoteVideoRef.current;
      const canvas = captureCanvasRef.current;
      if (!video || !canvas || video.readyState < 2) return null;

      canvas.width  = video.videoWidth  || 320;
      canvas.height = video.videoHeight || 240;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const base64 = canvas.toDataURL("image/jpeg", 0.8);

      const res = await fetch("https://www.d-rosa.ai/save_frame", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ frame: base64 }),
      });

      const data = await res.json();
      console.log("Frame saved:", data);
      return data;
    } catch (err) {
      console.error("Frame upload error:", err);
      return null;
    }
  };

  // -- Compare saved frame with limit image -----------------------------------
  const checkComparison = async (relayNo) => {
    try {
      const response = await fetch(
        `https://www.d-rosa.ai/compare_limit_frame?limit_no=${relayNo}`
      );
      const data = await response.json();
      console.log(`Compare result (limit${relayNo}):`, data);

      if (data.status === "success") {

    setResult(data.result);
    setSimilarity(data.similarity);

    log(`Relay ${relayNo}: ${data.result} (${data.similarity})`);

    // Robot reached this limit successfully
    if (data.result === "Matched") {
        
        setBoundaryMessage(`Relay ${relayNo} reached safely.`);
if (pcRef.current?.dataChannel?.readyState === "open") {
    pcRef.current.dataChannel.send(JSON.stringify({
        type: "comparison",
        relay: relayNo,
        status: "matched",
        similarity: data.similarity,
        message: `Relay ${relayNo} reached safely.`
    }));
}

        setDisabledRelays(prev => {
            const updated = [...prev];
            updated[relayNo - 1] = true;
            return updated;
        });

  // Make relay OFF in UI
    setRelayStates(prev => {
        const updated = [...prev];
        updated[relayNo - 1] = false;
        return updated;
    });

// Turn OFF physical relay
    if (btCharRef.current) {
        try {
            await sendBTCommand(btCharRef.current, `OFF${relayNo}`);
            log(`BT OFF${relayNo} sent automatically`);
        } catch (err) {
            log(`Failed to send OFF${relayNo}: ${err.message}`);
        }
    }

    // Inform remote user
    if (pcRef.current?.dataChannel?.readyState === "open") {
        pcRef.current.dataChannel.send(
            JSON.stringify({
                type: "relay",
                relay: relayNo,
                action: "off",
            })
        );
    }

        // stop checking this relay
        setActiveRelay(null);

        log(`Relay ${relayNo} completed successfully.`);
    }

    // Robot crossed boundary
    else {

        log(`Robot crossed Boundary ${relayNo}`);

        setBoundaryMessage(`Robot crossed Boundary ${relayNo}`);

if (pcRef.current?.dataChannel?.readyState === "open") {
    pcRef.current.dataChannel.send(JSON.stringify({
        type: "comparison",
        relay: relayNo,
        status: "boundary",
        similarity: data.similarity,
        message: `Robot crossed Boundary ${relayNo}`
    }));
}
    }
} else {
        setResult("Error");
        
      }
    } catch (err) {
      console.error("Comparison error:", err);
      setResult("Error");
      setSimilarity(null);
    }
  };

  // -- Run comparison every 2s when activeRelay is set ------------------------
  useEffect(() => {
    if (!activeRelay) return;
    const interval = setInterval(() => checkComparison(activeRelay), 2000);
    return () => clearInterval(interval);
  }, [activeRelay]);

  // -- Send frame every 5s while joined --------------------------------------
  useEffect(() => {
    if (!joined) return;
    const interval = setInterval(() => captureAndSendFrame(), 5000);
    return () => clearInterval(interval);
  }, [joined]);

  // -- Relay trigger ----------------------------------------------------------
const triggerRelay = async (relay) => {

if (disabledRelays[relay-1]) {

    log(`Relay ${relay} already completed.`);

    return;
}
  const isOn   = relayStates[relay - 1];
  const action = isOn ? "off" : "on";
  const command = `${action.toUpperCase()}${relay}`;

  if (!isOn) {
    // -- Send a fresh frame RIGHT NOW before starting comparison --
    log(`Capturing fresh frame for Relay ${relay} comparison�`);
    await captureAndSendFrame();   // wait for this to complete first

    setBoundaryMessage("");

    setActiveRelay(relay);

    log(`Comparison started for Relay ${relay}`);
  } else {
    setActiveRelay(null);
    setResult("Waiting...");
    setSimilarity(null);
    log(`Comparison stopped for Relay ${relay}`);
  }

  setRelayStates(prev => {
    const u = [...prev];
    u[relay - 1] = !u[relay - 1];
    return u;
  });

  // BT trigger
  if (btCharRef.current) {
    try {
      await sendBTCommand(btCharRef.current, command);
      log(`BT ${command}`);
    } catch (err) {
      log(`BT send failed: ${err.message}`);
    }
  } else {
    log(`BT not connected � ${command} not sent via BT`);
  }

  // Notify peer
  if (pcRef.current?.dataChannel?.readyState === "open") {
    pcRef.current.dataChannel.send(JSON.stringify({ type: "relay", relay, action }));
    log(`DC Relay ${relay} ${action.toUpperCase()} sent to peer`);
  }
};
  // -- DataChannel ------------------------------------------------------------
  const setupDataChannel = (channel) => {
    channel.onopen = () => log("DataChannel connected");

    channel.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
// ---------------- Comparison Sync ----------------
if (data.type === "comparison") {

    setBoundaryMessage(data.message);

    setResult(
        data.status === "matched"
            ? "Matched"
            : "Boundary Crossed"
    );

    setSimilarity(data.similarity);

    if (data.status === "matched") {
        setDisabledRelays(prev => {
            const updated = [...prev];
            updated[data.relay - 1] = true;
            return updated;
        });

        setRelayStates(prev => {
            const updated = [...prev];
            updated[data.relay - 1] = false;
            return updated;
        });
    }

    return;
}



        if (data.type === "relay") {
          const { relay, action } = data;
          const command = `${action.toUpperCase()}${relay}`;
          setRelayStates(prev => {
            const u = [...prev];
            u[relay - 1] = action === "on";
            return u;
          });
          if (btCharRef.current) {
            try {
              await sendBTCommand(btCharRef.current, command);
              log(`BT (from peer) ${command}`);
            } catch (err) {
              log(`BT (from peer) failed: ${err.message}`);
            }
          } else {
            log(`Peer Relay ${relay} ${action.toUpperCase()} (BT not connected here)`);
          }
        }
      } catch (err) {
        console.error("DataChannel parse error:", err);
      }
    };

    channel.onerror = (e) => console.error("DC Error", e);
    channel.onclose = () => log("DataChannel closed");
    pcRef.current.dataChannel = channel;
  };

  // -- WebRTC helpers ---------------------------------------------------------
  const cleanupPeer = () => {
    pcRef.current?.close();
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    pcRef.current = null;
    localStreamRef.current = null;
    setJoined(false);
    setInitiator(false);
    setActiveRelay(null);
    setResult("Waiting...");
    setSimilarity(null);
  };

  const startCall = async () => {
    const channel = pcRef.current.createDataChannel("relayChannel");
    setupDataChannel(channel);
    const offer = await pcRef.current.createOffer();
    await pcRef.current.setLocalDescription(offer);
    socket.emit("signal", { type: "offer", offer, room, user });
  };

  const handleJoin = () => {
    cleanupPeer();
    socket.emit("join", { room, user });
    setJoined(true);
  };

  const handleEndCall = () => {
    cleanupPeer();
    log("Call ended");
  };

  const handleToggleAudio = () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
  };

  const handleToggleVideo = () => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  };

  const handleShareScreen = async () => {
    if (!pcRef.current || !localStreamRef.current) return;
    if (!screenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack = screenStream.getVideoTracks()[0];
        screenTrackRef.current = screenTrack;
        const sender = pcRef.current.getSenders().find(s => s.track && s.track.kind === "video");
        if (sender) sender.replaceTrack(screenTrack);
        screenTrack.onended = () => handleStopShare();
        setScreenSharing(true);
      } catch (err) {
        log(`Share Screen failed: ${err.message}`);
      }
    } else {
      handleStopShare();
    }
  };

  const handleStopShare = () => {
    try {
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      const sender = pcRef.current?.getSenders().find(s => s.track && s.track.kind === "video");
      if (sender && videoTrack) sender.replaceTrack(videoTrack);
      screenTrackRef.current?.stop();
      screenTrackRef.current = null;
    } catch (err) {
      console.error("Stop share error:", err);
    }
    setScreenSharing(false);
  };

  // -- WebRTC effect ----------------------------------------------------------
  useEffect(() => {
    if (!joined) return;

    let cancelled = false;

    const init = async () => {
      let attempts = 0;
      while ((!localVideoRef.current || !remoteVideoRef.current) && attempts < 20) {
        await new Promise(r => setTimeout(r, 100));
        attempts++;
      }

      if (cancelled) return;

      if (!localVideoRef.current || !remoteVideoRef.current) {
        log("Video elements not found � try rejoining");
        return;
      }

      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:webrtc.d-rosa.ai:3478" },
          {
            urls: [
              "turn:webrtc.d-rosa.ai:3478?transport=udp",
              "turn:webrtc.d-rosa.ai:3478?transport=tcp",
            ],
            username: "rosa",
            credential: "1234",
          },
        ],
        iceCandidatePoolSize: 10,
      });

      pcRef.current = pc;

      pc.onconnectionstatechange    = () => console.log("Peer:", pc.connectionState);
      pc.oniceconnectionstatechange = () => console.log("ICE:",  pc.iceConnectionState);

      pc.onicecandidate = (event) => {
        if (event.candidate)
          socket.emit("signal", { type: "ice", candidate: event.candidate, room, user });
      };

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }

      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (e) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0];
      };

      pc.ondatachannel = (e) => setupDataChannel(e.channel);

      socket.on("signal", async (data) => {
        try {
          if (data.type === "offer") {
            await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
            while (pendingCandidatesRef.current.length)
              await pc.addIceCandidate(pendingCandidatesRef.current.shift());
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit("signal", { type: "answer", answer, room, user });
          } else if (data.type === "answer") {
            await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          } else if (data.type === "ice") {
            const candidate = new RTCIceCandidate(data.candidate);
            if (pc.remoteDescription) await pc.addIceCandidate(candidate);
            else pendingCandidatesRef.current.push(candidate);
          }
        } catch (e) { console.error("Signal Error", e); }
      });

      socket.on("initiator", (data) => {
        if (data.initiatorUser === user) setInitiator(true);
      });
    };

    init();

    return () => {
      cancelled = true;
      socket.off("signal");
      socket.off("initiator");
      cleanupPeer();
    };
  }, [joined]);

  useEffect(() => {
    if (joined && initiator) setTimeout(() => startCall(), 1500);
  }, [initiator]);

  // -- Relay button -----------------------------------------------------------
  const relayButton = (relay) => (
    <button
    key={relay}
    disabled={disabledRelays[relay-1]}
    onClick={() => triggerRelay(relay)}
      style={{
        width:        isMobile ? "70px" : "90px",
        height:       isMobile ? "58px" : "75px",
        margin:       isMobile ? "4px"  : "8px",
        borderRadius: "18px",
        border:       activeRelay === relay ? "3px solid #fff" : "none",
        background:
disabledRelays[relay-1]
    ? "#9E9E9E"
    : relayStates[relay-1]
    ? "#00c853"
    : "#ff4081",
opacity: disabledRelays[relay-1] ? 0.6 : 1,

cursor: disabledRelays[relay-1]
    ? "not-allowed"
    : "pointer",
        color:        "white",
        fontWeight:   "bold",
        fontSize:     isMobile ? "12px" : "14px",
       
        boxShadow:    activeRelay === relay
          ? "0 0 14px 4px rgba(255,255,255,0.8)"
          : "0 4px 10px rgba(0,0,0,.25)",
        transition:   "all .3s",
      }}
    >
      Relay {relay}<br />{relayStates[relay - 1] ? "ON" : "OFF"}
    </button>
  );

  // -- Bluetooth bar ----------------------------------------------------------
  const BTBar = () => (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      gap: "12px", margin: "16px 0", flexWrap: "wrap",
    }}>
      <div style={{
        padding: "8px 20px", borderRadius: "20px",
        background: btConnected ? "#e8f5e9" : "#fce4ec",
        border: `2px solid ${btConnected ? "#00c853" : "#ff4081"}`,
        fontWeight: "bold", fontSize: "14px",
        color: btConnected ? "#1b5e20" : "#880e4f",
      }}>
        {btConnected ? `BT: ${btDevice?.name ?? "Connected"}` : "BT: Disconnected"}
      </div>
      {!btConnected ? (
        <button
          onClick={handleBTConnect}
          disabled={btConnecting}
          style={{
            padding: "8px 22px", borderRadius: "20px", border: "none",
            background: btConnecting ? "#90caf9" : "#1565c0",
            color: "white", fontWeight: "bold",
            cursor: btConnecting ? "default" : "pointer",
          }}
        >
          {btConnecting ? "Connecting�" : "Connect Bluetooth"}
        </button>
      ) : (
        <button
          onClick={handleBTDisconnect}
          style={{
            padding: "8px 22px", borderRadius: "20px", border: "none",
            background: "#d32f2f", color: "white",
            fontWeight: "bold", cursor: "pointer",
          }}
        >
          Disconnect BT
        </button>
      )}
{boundaryMessage && (
  <div
    style={{
      width: "100%",
      marginTop: "12px",
      padding: "12px",
      borderRadius: "10px",
      textAlign: "center",
      fontWeight: "bold",
      fontSize: "16px",
      background:
        boundaryMessage.includes("crossed")
          ? "#ffebee"
          : "#e8f5e9",
      color:
        boundaryMessage.includes("crossed")
          ? "#c62828"
          : "#2e7d32",
      border:
        boundaryMessage.includes("crossed")
          ? "2px solid #c62828"
          : "2px solid #2e7d32",
    }}
  >
    {boundaryMessage}
  </div>
)}

    </div>
  );
  // -- Comparison result bar --------------------------------------------------
  const CompareBar = () => (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      gap: "16px", margin: "12px 0", flexWrap: "wrap",
    }}>
      <div style={{
        padding: "8px 24px", borderRadius: "20px", fontWeight: "bold", fontSize: "15px",
        background: result === "Matched"    ? "#e8f5e9"
                  : result === "Waiting..." ? "#f5f5f5"
                  : "#fce4ec",
        border: `2px solid ${
          result === "Matched"    ? "#00c853"
          : result === "Waiting..." ? "#bdbdbd"
          : "#d32f2f"}`,
        color: result === "Matched"    ? "#1b5e20"
             : result === "Waiting..." ? "#616161"
             : "#b71c1c",
      }}>
        {result === "Matched" ? "?" : result === "Waiting..." ? "?" : "?"} {result}
        {similarity !== null && (
          <span style={{ marginLeft: "10px", fontSize: "13px" }}>({similarity})</span>
        )}
      </div>
      {activeRelay && (
        <div style={{
          padding: "6px 16px", borderRadius: "20px",
          background: "#fff9c4", border: "2px solid #f9a825",
          fontWeight: "bold", fontSize: "13px", color: "#f57f17",
        }}>
          Comparing Relay {activeRelay}
        </div>
      )}
    </div>
  );

  const ctrlBtn = (bg) => ({
    padding: "9px 10px", borderRadius: "15px", border: "none",
    background: bg, color: "white", fontSize: "12px",
    fontWeight: "bold", cursor: "pointer", flex: "1 1 0",
  });


const crosshairStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40px",
  height: "40px",
  pointerEvents: "none",
  zIndex: 100,
};
const topLine = {
  position: "absolute",
  width: "2px",
  height: "12px",
  background: "red",
  left: "19px",
  top: "0px",
};

const bottomLine = {
  position: "absolute",
  width: "2px",
  height: "12px",
  background: "red",
  left: "19px",
  bottom: "0px",
};

const leftLine = {
  position: "absolute",
  width: "12px",
  height: "2px",
  background: "red",
  left: "0px",
  top: "19px",
};

const rightLine = {
  position: "absolute",
  width: "12px",
  height: "2px",
  background: "red",
  right: "0px",
  top: "19px",
};

  // ==========================================================================
  // JOIN SCREEN
  // ==========================================================================
  if (!joined) {
    return (
      <div style={{ minHeight: "100vh", padding: "20px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
        <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "38px", marginBottom: "5px" }}>ROSA A</h1>
        <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "20px", fontSize: "18px" }}>User : {user}</div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <div style={{
            background: "white", padding: "40px", borderRadius: "30px",
            width: "350px", textAlign: "center", boxShadow: "0 8px 30px rgba(0,0,0,.2)",
          }}>
            <h2>Join Room</h2>
            <input
              placeholder="Enter Room"
              value={room}
              onChange={e => setRoom(e.target.value)}
              style={{ width: "90%", padding: "15px", borderRadius: "15px", border: "1px solid #ddd", fontSize: "16px" }}
            />
            <br />
            <button
              onClick={handleJoin}
              style={{
                marginTop: "25px", padding: "12px 40px", border: "none",
                borderRadius: "20px", background: "#1565c0",
                color: "white", fontSize: "18px", cursor: "pointer",
              }}
            >
              Join
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // MOBILE LAYOUT
  // ==========================================================================
  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", padding: "12px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
        <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "28px", margin: "0 0 4px" }}>ROSA A</h1>
        <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "8px", fontSize: "13px" }}>User : {user}</div>

        <BTBar />
        <CompareBar />

        {/* Row 1: Two videos side by side */}
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "10px" }}>
          <div style={{ background: "white", padding: "6px", borderRadius: "16px", flex: 1 }}>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", marginBottom: "4px" }}>Local</div>
            <video ref={localVideoRef} autoPlay muted playsInline
              style={{ width: "100%", height: "200px", borderRadius: "12px", border: "3px solid green", objectFit: "cover", display: "block" }} />
          </div>
          <div style={{ background: "white", padding: "6px", borderRadius: "16px", flex: 1, position: "relative" }}>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", marginBottom: "4px" }}>Remote</div>
            <video ref={remoteVideoRef} autoPlay playsInline
              style={{ width: "100%", height: "200px", borderRadius: "12px", border: "3px solid blue", objectFit: "cover", display: "block" }} />
            <div style={crosshairStyle}>
  <div style={topLine}></div>
  <div style={bottomLine}></div>
  <div style={leftLine}></div>
  <div style={rightLine}></div>
</div>
          </div>
        </div>

        {/* Row 2: Call controls */}
        <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "12px" }}>
          <button onClick={handleToggleAudio} style={ctrlBtn("#1565c0")}>{audioEnabled  ? "Mute"       : "Unmute"   }</button>
          <button onClick={handleToggleVideo} style={ctrlBtn("#1565c0")}>{videoEnabled  ? "Camera Off" : "Camera On"}</button>
          <button onClick={handleShareScreen} style={ctrlBtn("#1565c0")}>{screenSharing ? "Stop Share" : "Share"    }</button>
          <button onClick={handleEndCall}     style={ctrlBtn("#d32f2f")}>End Call</button>
        </div>

        {/* Row 3: Relays 1�4 */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "4px" }}>
          {[1,2,3,4].map(r => relayButton(r))}
        </div>

        {/* Row 4: Relays 5�8 */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
          {[5,6,7,8].map(r => relayButton(r))}
        </div>

        {/* Command Log */}
        <div style={{
          background: "white", padding: "14px", borderRadius: "16px",
          height: "150px", overflow: "auto", boxShadow: "0 4px 15px rgba(0,0,0,.15)",
        }}>
          <h3 style={{ margin: "0 0 6px", fontSize: "14px" }}>Command Log</h3>
          {commandLog.map((entry, i) =>
            <div key={i} style={{ fontSize: "11px", fontFamily: "monospace" }}>{entry}</div>
          )}
        </div>

        <canvas ref={captureCanvasRef} style={{ display: "none" }} />
      </div>
    );
  }

  // ==========================================================================
  // DESKTOP LAYOUT
  // ==========================================================================
  return (
    <div style={{ minHeight: "100vh", padding: "20px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
      <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "38px", marginBottom: "5px" }}>ROSA A</h1>
      <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "20px", fontSize: "18px" }}>User : {user}</div>

      <BTBar />
      <CompareBar />

      {/* Video + side relays */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {relayButton(1)}{relayButton(2)}
        </div>

        <div style={{ background: "white", padding: "12px", borderRadius: "25px", boxShadow: "0 6px 20px rgba(0,0,0,.2)" }}>
          <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "8px" }}>Local</div>
          <video ref={localVideoRef} autoPlay muted playsInline
            style={{ width: "320px", height: "240px", borderRadius: "20px", border: "4px solid green", objectFit: "cover" }} />
        </div>

       <div
  style={{
    background: "white",
    padding: "12px",
    borderRadius: "25px",
    boxShadow: "0 6px 20px rgba(0,0,0,.2)",
  }}
>
  <div
    style={{
      textAlign: "center",
      fontWeight: "bold",
      marginBottom: "8px",
    }}
  >
    Remote
  </div>

  <div
    style={{
      position: "relative",
      width: "320px",
      height: "240px",
    }}
  >
    <video
      ref={remoteVideoRef}
      autoPlay
      playsInline
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "20px",
        border: "4px solid blue",
        objectFit: "cover",
        display: "block",
      }}
    />

    <div style={crosshairStyle}>
      <div style={topLine}></div>
      <div style={bottomLine}></div>
      <div style={leftLine}></div>
      <div style={rightLine}></div>
    </div>
  </div>
</div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {relayButton(3)}{relayButton(4)}
        </div>
      </div>

      {/* Call controls */}
      <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "30px", flexWrap: "wrap" }}>
        <button onClick={handleToggleAudio}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {audioEnabled ? "Mute" : "Unmute"}
        </button>
        <button onClick={handleToggleVideo}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {videoEnabled ? "Camera Off" : "Camera On"}
        </button>
        <button onClick={handleShareScreen}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {screenSharing ? "Stop Share" : "Share Screen"}
        </button>
        <button onClick={handleEndCall}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#d32f2f", color: "white" }}>
          End Call
        </button>
      </div>

      {/* Bottom relays */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "30px", gap: "20px", flexWrap: "wrap" }}>
        {relayButton(5)}{relayButton(6)}{relayButton(7)}{relayButton(8)}
      </div>

      {/* Command log */}
      <div style={{
        marginTop: "30px", background: "white", padding: "20px",
        borderRadius: "20px", height: "180px", overflow: "auto",
        boxShadow: "0 4px 15px rgba(0,0,0,.15)",
      }}>
        <h3>Command Log</h3>
        {commandLog.map((entry, i) =>
          <div key={i} style={{ fontSize: "13px", fontFamily: "monospace" }}>{entry}</div>
        )}
      </div>

      <canvas ref={captureCanvasRef} style={{ display: "none" }} />
    </div>
  );
}

export default Run;